import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { DollarSign, Download, Store, Users, BarChart3, Clock, Video, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { parseLocalDate } from '../../utils/dateUtils';

const AdminStatisticsTab = ({ platformStats }) => {
  const { formatPrice } = useCurrency();
  const { t } = useTranslation();

  const [eventStats, setEventStats] = useState(null);
  const [statsMonths, setStatsMonths] = useState(12);
  const [downloadingList, setDownloadingList] = useState(null);
  const [couponStats, setCouponStats] = useState(null);
  const [loadingCouponStats, setLoadingCouponStats] = useState(false);

  useEffect(() => {
    fetchEventStats(statsMonths);
  }, []);

  const fetchEventStats = async (months = 12) => {
    try {
      const response = await axios.get(`${API}/stats/admin/events?months=${months}`);
      setEventStats(response.data);
    } catch (error) {
      console.error('Failed to fetch event stats', error);
      toast.error('Failed to load event statistics');
    }
  };

  const fetchCouponStats = async () => {
    setLoadingCouponStats(true);
    try {
      const response = await axios.get(`${API}/stats/admin/coupons`);
      setCouponStats(response.data);
    } catch (error) {
      console.error('Failed to fetch coupon stats', error);
      toast.error('Failed to load coupon statistics');
    } finally {
      setLoadingCouponStats(false);
    }
  };

  const downloadCouponReport = async (format = 'csv') => {
    setDownloadingList('coupons');
    try {
      const response = await axios.get(`${API}/admin/download/coupons?format=${format}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coupon_report_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Coupon report downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Failed to download coupon report', error);
      toast.error('Failed to download coupon report');
    } finally {
      setDownloadingList(null);
    }
  };

  const downloadExhibitors = async (eventId = null, format = 'csv') => {
    setDownloadingList('exhibitors');
    try {
      const params = new URLSearchParams();
      if (eventId) params.append('event_id', eventId);
      params.append('format', format);
      const response = await axios.get(`${API}/admin/download/exhibitors?${params}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exhibitors_${eventId || 'all'}_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Exhibitors list downloaded');
    } catch (error) {
      toast.error('Failed to download exhibitors list');
    } finally {
      setDownloadingList(null);
    }
  };

  const downloadAttendees = async (eventId = null, format = 'csv') => {
    setDownloadingList('attendees');
    try {
      const params = new URLSearchParams();
      if (eventId) params.append('event_id', eventId);
      params.append('format', format);
      const response = await axios.get(`${API}/admin/download/attendees?${params}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendees_${eventId || 'all'}_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Attendees list downloaded');
    } catch (error) {
      toast.error('Failed to download attendees list');
    } finally {
      setDownloadingList(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Event Statistics & Downloads Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                {t('dashboard.events_stats_income')}
              </CardTitle>
              <CardDescription>{t('dashboard.registrations')} & {t('dashboard.revenue')}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(statsMonths)} onValueChange={(v) => { setStatsMonths(Number(v)); fetchEventStats(Number(v)); }}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Last 3 months</SelectItem>
                  <SelectItem value="6">Last 6 months</SelectItem>
                  <SelectItem value="12">Last 12 months</SelectItem>
                  <SelectItem value="24">Last 24 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {eventStats ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium">{t('dashboard.total_events')}</p>
                  <p className="text-2xl font-bold text-blue-700">{eventStats.summary.total_events}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-orange-600 font-medium">{t('dashboard.total_exhibitors')}</p>
                  <p className="text-2xl font-bold text-orange-700">{eventStats.summary.total_exhibitors}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600 font-medium">{t('nav.attendees')}</p>
                  <p className="text-2xl font-bold text-purple-700">{eventStats.summary.total_attendees}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">{t('dashboard.exhibitor_registrations')}</p>
                  <p className="text-2xl font-bold text-green-700">{formatPrice(eventStats.summary.total_exhibitor_income)}</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4">
                  <p className="text-sm text-teal-600 font-medium">{t('dashboard.attendee_registrations')}</p>
                  <p className="text-2xl font-bold text-teal-700">{formatPrice(eventStats.summary.total_attendee_income)}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-indigo-600 font-medium">{t('dashboard.revenue')}</p>
                  <p className="text-2xl font-bold text-indigo-700">{formatPrice(eventStats.summary.total_income)}</p>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 mr-2 self-center w-full sm:w-auto">Download All:</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => downloadExhibitors(null, 'csv')}
                  disabled={downloadingList === 'exhibitors'}
                >
                  <Download className="h-4 w-4 mr-1" /> 
                  {downloadingList === 'exhibitors' ? 'Downloading...' : 'Exhibitors CSV'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => downloadExhibitors(null, 'xlsx')}
                  disabled={downloadingList === 'exhibitors'}
                >
                  <Download className="h-4 w-4 mr-1" /> Exhibitors Excel
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => downloadAttendees(null, 'csv')}
                  disabled={downloadingList === 'attendees'}
                >
                  <Download className="h-4 w-4 mr-1" /> 
                  {downloadingList === 'attendees' ? 'Downloading...' : 'Attendees CSV'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => downloadAttendees(null, 'xlsx')}
                  disabled={downloadingList === 'attendees'}
                >
                  <Download className="h-4 w-4 mr-1" /> Attendees Excel
                </Button>
              </div>

              {/* Per-Event Stats Table */}
              {eventStats.events?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">{t('dashboard.per_event_breakdown')}</h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('dashboard.event')}</TableHead>
                          <TableHead>{t('dashboard.status')}</TableHead>
                          <TableHead>{t('dashboard.date')}</TableHead>
                          <TableHead className="text-right">{t('dashboard.exhibitors')}</TableHead>
                          <TableHead className="text-right">{t('nav.attendees')}</TableHead>
                          <TableHead className="text-right">{t('dashboard.exhibitor_registrations')}</TableHead>
                          <TableHead className="text-right">{t('dashboard.attendee_registrations')}</TableHead>
                          <TableHead className="text-right">{t('dashboard.revenue')}</TableHead>
                          <TableHead>{t('dashboard.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {eventStats.events.map((event) => (
                          <TableRow key={event.event_id}>
                            <TableCell className="font-medium">{event.event_name}</TableCell>
                            <TableCell>
                              <Badge className={
                                event.status === 'live' ? 'bg-green-100 text-green-700' :
                                event.status === 'registration_open' ? 'bg-amber-100 text-amber-700' :
                                event.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {event.status?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {parseLocalDate(event.start_date).toLocaleDateString()} - {parseLocalDate(event.end_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">{event.exhibitor_count}</TableCell>
                            <TableCell className="text-right">{event.attendee_count}</TableCell>
                            <TableCell className="text-right text-green-600">{formatPrice(event.exhibitor_income)}</TableCell>
                            <TableCell className="text-right text-teal-600">{formatPrice(event.attendee_income)}</TableCell>
                            <TableCell className="text-right font-semibold">{formatPrice(event.total_income)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => downloadExhibitors(event.event_id, 'csv')}
                                  title="Download exhibitors for this event"
                                >
                                  <Store className="h-3 w-3" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => downloadAttendees(event.event_id, 'csv')}
                                  title="Download attendees for this event"
                                >
                                  <Users className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Loading event statistics...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coupon Usage Statistics Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Coupon Usage Report
              </CardTitle>
              <CardDescription>Track promotional coupon usage across events</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadCouponReport('csv')}
                disabled={downloadingList === 'coupons' || !couponStats}
              >
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadCouponReport('xlsx')}
                disabled={downloadingList === 'coupons' || !couponStats}
              >
                <Download className="h-4 w-4 mr-1" />
                Excel
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchCouponStats}
                disabled={loadingCouponStats}
              >
                {loadingCouponStats ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {couponStats ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium">Total Coupons</p>
                  <p className="text-2xl font-bold text-blue-700">{couponStats.summary.total_coupons}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">Active</p>
                  <p className="text-2xl font-bold text-green-700">{couponStats.summary.active_coupons}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-red-600 font-medium">Expired</p>
                  <p className="text-2xl font-bold text-red-700">{couponStats.summary.expired_coupons}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600 font-medium">Total Uses</p>
                  <p className="text-2xl font-bold text-purple-700">{couponStats.summary.total_uses}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-orange-600 font-medium">Discount Given</p>
                  <p className="text-2xl font-bold text-orange-700">{formatPrice(couponStats.summary.total_discount_given)}</p>
                </div>
              </div>

              {/* Coupon Details Table */}
              {couponStats.coupons?.length > 0 ? (
                <div>
                  <h4 className="font-semibold mb-3">Coupon Details</h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Event</TableHead>
                          <TableHead>Coupon Code</TableHead>
                          <TableHead className="text-center">Discount</TableHead>
                          <TableHead className="text-center">Applies To</TableHead>
                          <TableHead className="text-center">Attendee Uses</TableHead>
                          <TableHead className="text-center">Exhibitor Uses</TableHead>
                          <TableHead className="text-center">Total Uses</TableHead>
                          <TableHead className="text-center">Max Uses</TableHead>
                          <TableHead className="text-right">Est. Discount</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {couponStats.coupons.map((coupon) => (
                          <TableRow key={coupon.event_id}>
                            <TableCell className="font-medium max-w-[200px] truncate" title={coupon.event_name}>
                              {coupon.event_name}
                            </TableCell>
                            <TableCell>
                              <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                                {coupon.coupon_code}
                              </code>
                            </TableCell>
                            <TableCell className="text-center font-semibold text-green-600">
                              {coupon.discount_percent}%
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-xs">
                                {coupon.applies_to === 'both' ? 'All' : 
                                 coupon.applies_to === 'attendees' ? 'Attendees' : 'Exhibitors'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">{coupon.attendee_uses}</TableCell>
                            <TableCell className="text-center">{coupon.exhibitor_uses}</TableCell>
                            <TableCell className="text-center font-semibold">{coupon.total_uses}</TableCell>
                            <TableCell className="text-center text-slate-500">
                              {coupon.max_uses || '\u221e'}
                            </TableCell>
                            <TableCell className="text-right text-orange-600">
                              {formatPrice(coupon.estimated_discount_given)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={
                                coupon.status === 'active' ? 'bg-green-100 text-green-700' :
                                coupon.status === 'expired' ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {coupon.status === 'maxed_out' ? 'Max Reached' : coupon.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <Ticket className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No coupons configured yet</p>
                  <p className="text-sm">Enable coupons in event settings to see usage data</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Ticket className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Click "Refresh" to load coupon statistics</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Video Call Statistics Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {t('dashboard.video_call_statistics')}
          </CardTitle>
          <CardDescription>{t('dashboard.video_call_stats_desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {platformStats ? (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium">{t('dashboard.total_calls')}</p>
                  <p className="text-3xl font-bold text-blue-700">{platformStats.total_calls}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">{t('dashboard.completed')}</p>
                  <p className="text-3xl font-bold text-green-700">{platformStats.completed_calls}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-red-600 font-medium">{t('dashboard.abandoned') || 'Abandoned'}</p>
                  <p className="text-3xl font-bold text-red-700">{platformStats.abandoned_calls}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600 font-medium">{t('dashboard.completion_rate') || 'Completion Rate'}</p>
                  <p className="text-3xl font-bold text-purple-700">{platformStats.completion_rate}%</p>
                </div>
              </div>

              {/* Duration Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">{t('dashboard.avg_duration')}</span>
                  </div>
                  <p className="text-2xl font-bold">
                    {Math.floor(platformStats.average_duration_seconds / 60)}m {platformStats.average_duration_seconds % 60}s
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Video className="h-4 w-4" />
                    <span className="text-sm">{t('dashboard.total_calls')}</span>
                  </div>
                  <p className="text-2xl font-bold">
                    {platformStats.total_duration_hours}h
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Store className="h-4 w-4" />
                    <span className="text-sm">{t('dashboard.active_exhibitors') || 'Active Exhibitors'}</span>
                  </div>
                  <p className="text-2xl font-bold">{platformStats.booth_summary?.length || 0}</p>
                </div>
              </div>

              {/* Exhibitor Performance Table */}
              {platformStats.booth_summary?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">{t('dashboard.exhibitor_call_performance')}</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('dashboard.exhibitors')}</TableHead>
                        <TableHead>{t('dashboard.total_calls')}</TableHead>
                        <TableHead>{t('dashboard.completed')}</TableHead>
                        <TableHead>{t('dashboard.completion_rate') || 'Completion Rate'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {platformStats.booth_summary.map((booth) => (
                        <TableRow key={booth.booth_id}>
                          <TableCell className="font-medium">{booth.company_name}</TableCell>
                          <TableCell>{booth.total_calls}</TableCell>
                          <TableCell>{booth.completed_calls}</TableCell>
                          <TableCell>
                            <Badge className={
                              booth.completion_rate >= 80 ? 'bg-green-100 text-green-700' :
                              booth.completion_rate >= 50 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {booth.completion_rate}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No video call statistics available yet.</p>
              <p className="text-sm">Statistics will appear once exhibitors start handling video calls.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStatisticsTab;
